import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  QueryList,
  ViewChildren
} from '@angular/core';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { WebMitekCaptureModalComponent } from '@components/web-mitek-capture-modal/web-mitek-capture-modal.component';
import { ContextService } from '@core/services/core/context.service';
import { Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { MitekScannerService } from 'src/app/core/services/core/mitek-scanner.service';
import { UiHelperService } from 'src/app/core/services/core/ui-helper.service';
import { CompressImageService } from 'src/app/core/services/helpers/compress-image.service';
import { b64UrltoBlob, convertBlobTob64 } from 'src/app/core/utils/utils';

export enum DniError {
  COVER_NOT_CAPTURED = 'COVER_NOT_CAPTURED',
  UNVALID_EXTENSION = 'UNVALID_EXTENSION',
  MAX_SIZE_EXCEEDED = 'MAX_SIZE_EXCEEDED',
  GENERIC = 'GENERIC'
}

export enum FileOrigin {
  CAMERA,
  GALLERY,
  BOTH
}
export interface Cover {
  type: 'FRONT' | 'BACK';
  name: string;
  label: string;
  base64: string;
  blob: Blob;
}

export type DNI = { [key in 'FRONT' | 'BACK']: Cover };
@Component({
  selector: 'mapfre-update-dni',
  templateUrl: './mapfre-update-dni.component.html',
  styleUrls: ['./mapfre-update-dni.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    DatePipe
  ]
})
export class MapfreUpdateDNIComponent {
  @Input() attachIcon = 'assets/images/attach.svg';
  @Input() attachSuccessIcon = 'assets/images/result/success.svg';
  @Input() origin: keyof typeof FileOrigin = 'BOTH';
  @Input() maxSizeMB = 3;
  @Input() supportedImageExtensions = [
    'image/png',
    'image/jpg',
    'image/jpeg',
    'image/tif',
    'image/bmp'
  ];

  @Output() add = new EventEmitter<DNI>();
  @Output() error = new EventEmitter<DniError | string>();

  @ViewChildren('input', { read: ElementRef }) inputs: QueryList<ElementRef>;
  @ViewChildren('menu') menus: QueryList<MatMenu>;
  @ViewChildren('menuTrigger') menuTriggers: QueryList<MatMenuTrigger>;

  dni: DNI = {} as any;
  readonly covers: {
    i18n: string;
    type: 'FRONT' | 'BACK';
    loading: boolean;
  }[] = [
    {
      i18n: 'UPDATE_DOC.FRONT_LABEL',
      type: 'FRONT',
      loading: false
    },
    {
      i18n: 'UPDATE_DOC.BACK_LABEL',
      type: 'BACK',
      loading: false
    }
  ];

  constructor(
    public platform: Platform,
    private mitek: MitekScannerService,
    private cdr: ChangeDetectorRef,
    private trans: TranslateService,
    private compressService: CompressImageService,
    private ui: UiHelperService,
    public ctx: ContextService,
    private datePipe: DatePipe
  ) {}

  get files() {
    return Object.values(this.dni);
  }

  onDragEnter(event) {
    if (this.origin === 'CAMERA') {
      return;
    }
    event.preventDefault();
  }

  onDrop(event, index: number) {
    if (this.origin === 'CAMERA') {
      return;
    }
    const files = event.dataTransfer && event.dataTransfer.files;
    this.onFileSelectedFromGallery(files, index);
    event.preventDefault();
  }

  onDeleteItem({ type }: Cover) {
    delete this.dni[type];
    this.add.emit(this.dni);
  }

  onClickItem({ base64 }: Cover) {
    this.ui.previewImage(base64);
  }

  async onSelect(index: number) {
    if (this.origin === 'GALLERY' || !(await this.isCameraAvailable())){
      return  this.askForOriginOfFile(index);
    }
    if (this.origin === 'CAMERA') {
      return this.askFileThroughCamera(index);
    }
    this.askForOriginOfFile(index);
  }

  private async isCameraAvailable() {
    if (this.platform.is('cordova')) {
      // Unable to check in native if camera is available, mitek plugin will handle permissions
      return Promise.resolve(true);
    }
    if (!navigator.mediaDevices) {
      return Promise.resolve(false);
    }
    const devices = await navigator.mediaDevices.enumerateDevices();
    if (!devices || devices.length === 0) {
      return false;
    }

    return devices.find(device => device.kind === 'videoinput') !== undefined;
  }

  private askForOriginOfFile(index: number) {
    const menuTrigger = this.menuTriggers.toArray()[index];
    menuTrigger.openMenu();
  }

  askFileThroughGallery(index: number) {
    const input = this.inputs.toArray()[index];
    const { nativeElement } = input || ({} as any);
    if (nativeElement) {
      nativeElement.click();
    }
  }

  async askFileThroughCamera(index: number) {
    if (!this.platform.is('cordova')) {
      try {
        // Require permission before going to Mitek SDK. getUserMedia throws exception
        // if permissions are not granted by user. Otherwise (if the user grants access
        // to camera), tracks must be closed for cleaning up resources before going
        // to Mitek SDK.
        (
          await navigator.mediaDevices.getUserMedia({
            audio: false,
            video: true
          })
        )
          .getTracks()
          .forEach(track => track.stop());
      } catch (_) {
        this.ui.presentAlert(
          'Se requieren permisos para acceder a la cámara',
          'INFO'
        );
        return false;
      }
    }

    const { type } = this.covers[index];
    const resolver = this.captureMitekResolverFactory(type);

    this.proccessImage(resolver, index, 'image/jpeg');
    return true;
  }

  onFileSelectedFromGallery(files: File[], index: number) {
    const file = files && files[0];
    if (!file) {
      return;
    }
    this.proccessImage(
      this.selectedFromGalleryResolverFactory(file),
      index,
      file.type
    );
  }

  private captureMitekResolverFactory(type) {
    return this.platform.is('cordova')
      ? this.captureMitekResolverAPP(type)
      : this.captureMitekResolverWEB();
  }

  private captureMitekResolverAPP(type: 'FRONT' | 'BACK') {
    return () => this.mitek.capture(type);
  }

  private selectedFromGalleryResolverFactory(file: File) {
    return async () => {
      this.assertValidExtensionFile(file);
      return convertBlobTob64(file);
    };
  }

  private captureMitekResolverWEB() {
    return async () => {
      const modal = await this.ui.openComponentAsModal(WebMitekCaptureModalComponent, null, 'web-mitek-modal');
      const { data } = await modal.onWillDismiss();
      const { success, error } = data || ({} as any);
      if (error) {
        throw DniError.GENERIC;
      }

      return this.compressService.extractBase64BodyFromMetadata(success || '');
    };
  }

  private proccessImage(
    base64Resolver: () => Promise<string>,
    index: number,
    mimeType: string
  ) {
    const [input, cover] = [this.inputs.toArray()[index], this.covers[index]];
    cover.loading = true;

    base64Resolver()
      .then(base64Raw => this.assertNotEmpty(base64Raw))
      .then(base64Raw => this.compressService.buildBase64(base64Raw, mimeType))
      .then(base64 => this.createBlobFromBase64(base64))
      .then(data => this.assertLowerThanMaxSize(data))
      .then(({ base64 }) => base64)
      .then(base64 => this.createBlobFromBase64(base64))
      .then(data => this.assertValidExtensionBase64(data))
      .then(data => this.emitCover({ ...this.covers[index], ...data }))
      .catch(error => this.handleError(error))
      .finally(() => {
        input.nativeElement.value = null;
        cover.loading = false;
      });
  }

  private async createBlobFromBase64(base64: string) {
    return {
      base64,
      blob: await b64UrltoBlob(base64)
    };
  }

  private assertNotEmpty(base64: string) {
    if (base64) {
      return base64;
    } else {
      throw { errorCode: DniError.COVER_NOT_CAPTURED };
    }
  }

  private assertValidExtensionFile(file: File) {
    this.assertValidExtension(file.type);
    return file;
  }

  private assertValidExtensionBase64(data: { base64: string; blob: Blob }) {
    const { blob } = data;
    this.assertValidExtension(blob.type);
    return data;
  }

  private assertValidExtension(type: string) {
    if (this.supportedImageExtensions.includes(type)) {
      return;
    }
    throw { errorCode: DniError.UNVALID_EXTENSION };
  }

  private assertLowerThanMaxSize(data: { base64: string; blob: Blob }) {
    const { blob } = data;
    if (this.kbToMB(blob.size) <= this.maxSizeMB) {
      return data;
    } else {
      throw { errorCode: DniError.MAX_SIZE_EXCEEDED };
    }
  }

  private kbToMB(kb: number) {
    return kb / Math.pow(1024, 2);
  }

  private handleError(error) {
    const { errorCode } = error || ({} as any);

    if (errorCode === DniError.UNVALID_EXTENSION) {
      const message = 'UPDATE_DOC.UNVALID_EXTENSION';
      const extensions = this.supportedImageExtensions
        .map(ext => ext.replace('image/', ''))
        .join(' - ');

      this.ui.presentMessageAlert({
        message,
        interpolateParams: { extensions }
      });
    }
    if (errorCode === DniError.MAX_SIZE_EXCEEDED) {
      const message = 'UPDATE_DOC.MAX_SIZE_EXCEEDED';
      this.ui.presentMessageAlert({ message });
    }

    this.emitError(error);
  }

  private emitCover({ type, i18n, base64, blob }) {
    this.dni = {
      ...this.dni,
      [type]: {
        label: this.generateLabel(type),
        name: `${this.trans.instant(i18n)} - ${new Date().toISOString()}`,
        type,
        base64,
        blob
      }
    };
    this.add.emit(this.dni);
    this.cdr.markForCheck();
  }

  private generateLabel(type: 'FRONT' | 'BACK') {
    const i18nType = this.trans.instant('UPDATE_DOC.' + type);
    const date = this.datePipe.transform(new Date(), 'dd/MM/yyyy');
    return `${i18nType} ${date}`;
  }

  private emitError(error) {
    this.error.emit((error && error.errorCode) || DniError.GENERIC);
  }
}
